// ─────────────────────────────────────────────────────────────────────────────
// Thème MUI central — restyle les composants globalement (createTheme).
//
// Objectif : moderniser à grande échelle SANS toucher chaque écran. Le thème est
// construit à partir de la couleur du club (useClubTheme) et applique le "nouveau
// look" (référence Dashboard.js) aux composants MUI par défaut.
//
// PRUDENCE VOLONTAIRE :
//  - PAS d'override global de MuiPaper : Paper est la base de AppBar / Dialog /
//    Menu / Drawer (dont l'AppBar dégradé du Layout) — un override casserait ces
//    surfaces. On restyle donc MuiCard uniquement pour les "vraies" cartes.
//  - shape.borderRadius reste modéré (8px) pour ne pas rendre les inputs/menus
//    exagérément arrondis ; les cartes reçoivent 16px via l'override MuiCard.
//  - MuiCard applique le style STATIQUE (sans lift au survol) : les cartes
//    cliquables ajoutent leur hover via sx.
//  - Les écrans déjà stylés (Dashboard `sx={{...cardBase}}`, NewsTab styled(),
//    module Instat avec son propre Card) gardent la priorité : sx > styleOverrides.
// ─────────────────────────────────────────────────────────────────────────────
import { createTheme } from '@mui/material/styles';
import { cardStatic, PALETTE, INK, CANVAS } from './designSystem';

const FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const buildTheme = (primaryColor = '#F25F29', secondaryColor = '#FF7043') =>
  createTheme({
    palette: {
      primary: { main: primaryColor },
      secondary: { main: secondaryColor || primaryColor },
      // Couleurs sémantiques modernisées (façon Tailwind) — remplacent le rouge/
      // vert/orange Material par défaut pour color="error|success|warning|info".
      success: { main: PALETTE.emerald },
      error: { main: PALETTE.red },
      warning: { main: PALETTE.amber },
      info: { main: PALETTE.sky },
      text: { primary: INK },
      background: { default: CANVAS },
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: FONT_STACK,
      // Boutons : fini le SHOUTING UPPERCASE, poids un peu plus marqué.
      button: { textTransform: 'none', fontWeight: 600 },
      h4: { fontWeight: 800, color: INK },
      h5: { fontWeight: 800, color: INK },
      h6: { fontWeight: 700, color: INK },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: { root: { borderRadius: 10 } },
      },
      // Toutes les <Card> deviennent "cardBase statique" (16px, bordure douce,
      // ombre légère) au lieu de l'élévation Material par défaut.
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { ...cardStatic } },
      },
      // Onglets : indicateur + onglet actif à la couleur du club, casse normale.
      MuiTabs: {
        styleOverrides: {
          indicator: { backgroundColor: primaryColor },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            '&.Mui-selected': { color: primaryColor },
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 600 } },
      },
    },
  });

export default buildTheme;
